<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :draggable="false"
    :header="dialogHeader"
    :style="{ width: '560px' }"
    :breakpoints="{ '960px': '90vw' }"
    :closable="!isExporting"
    :dismissable-mask="!isExporting"
    @hide="resetProgress"
  >
    <!-- Section 1 — Summary of what's going into the report -->
    <section class="mb-6">
      <h3 class="text-sm font-medium text-slate-700 mb-3">
        {{ $t('messages.quickValuation.shortReport.summary') }}
      </h3>
      <ul class="text-sm text-slate-600 space-y-1.5">
        <li v-if="evaluation?.address" class="flex items-center gap-2">
          <span class="pi pi-check text-emerald-500 text-xs"></span>
          <span>{{ evaluation.address }}</span>
        </li>
        <li class="flex items-center gap-2">
          <span class="pi pi-check text-emerald-500 text-xs"></span>
          <span>{{ $t('messages.quickValuation.shortReport.includesPrediction') }}</span>
        </li>
        <li
          v-if="showComparablesLine"
          class="flex items-center gap-2"
          data-testid="qv-short-report-comparables"
        >
          <span class="pi pi-check text-emerald-500 text-xs"></span>
          <span>
            {{ $t('messages.quickValuation.shortReport.includesComparables', { count: comparableCount }) }}
          </span>
        </li>
        <li
          class="flex items-center gap-2"
          :class="livePhotoCount > 0 ? 'text-slate-600' : 'text-slate-400'"
        >
          <span
            class="text-xs"
            :class="livePhotoCount > 0 ? 'pi pi-check text-emerald-500' : 'pi pi-minus text-slate-300'"
          ></span>
          <span v-if="livePhotoCount > 0">
            {{ $t('messages.quickValuation.shortReport.photosWithCount', { count: livePhotoCount }) }}
          </span>
          <span v-else>{{ $t('messages.quickValuation.shortReport.noPhotos') }}</span>
        </li>
      </ul>
    </section>

    <!-- Section 2 — Template selection -->
    <section class="mb-6">
      <label class="block text-sm font-medium text-slate-700 mb-2">
        {{ $t('messages.quickValuation.shortReport.templateLabel') }}
      </label>
      <Select
        v-model="selectedTemplateId"
        data-testid="qv-short-report-template"
        :options="templates"
        option-label="description"
        option-value="id"
        :placeholder="$t('messages.quickValuation.shortReport.templatePlaceholder')"
        class="w-full"
        :disabled="isExporting || !templates.length"
      />
      <small class="text-slate-500 mt-1 block">
        {{ $t('messages.quickValuation.shortReport.templateHelp') }}
      </small>
    </section>

    <!-- Section 3 — Photos (RED-211) -->
    <ShortReportPhotoSection
      :evaluation-id="evaluation.id"
      @photos-changed="livePhotoCount = $event"
    />

    <!-- Section 4 — Kinnistusraamat (RED-203) -->
    <!-- RED-220: section stays visible even when the user lacks the
         `external-services` permission — they need to see the option and
         understand why it's disabled (banner explains: ask the admin or
         attach the excerpt manually to the exported Word file). -->
    <section class="mt-6 pt-4 border-t border-slate-200 flex flex-col gap-3">
      <div class="flex items-start gap-2">
        <Checkbox
          v-model="addLandRegistry"
          binary
          input-id="short-report-kr-add"
          data-testid="short-report-kr-add"
          :disabled="isExporting || landRegistryTestBlocked || landRegistryPermissionBlocked"
        />
        <label
          for="short-report-kr-add"
          class="text-sm leading-tight"
          :class="(landRegistryTestBlocked || landRegistryPermissionBlocked) ? 'text-slate-400' : 'text-slate-700'"
        >
          {{ $t('messages.quickValuation.shortReport.landRegistry.add', { cost: landRegistryCost }) }}
        </label>
      </div>
      <!-- RED-220: permission gate. Surfaced above the test-env guard because
           lacking `external-services` is the more fundamental block. -->
      <Message
        v-if="landRegistryPermissionBlocked"
        severity="warn"
        :closable="false"
        data-testid="short-report-kr-permission-blocked"
        class="border border-amber-300"
      >
        <div class="flex items-start gap-2">
          <span class="pi pi-lock text-amber-600 mt-0.5"></span>
          <div class="flex flex-col gap-1">
            <span class="font-semibold">
              {{ $t('messages.quickValuation.shortReport.landRegistry.permissionDeniedTitle') }}
            </span>
            <span class="text-sm">
              {{ $t('messages.quickValuation.shortReport.landRegistry.permissionDenied') }}
            </span>
          </div>
        </div>
      </Message>
      <!-- RED-213: hard block in non-LIVE for non-allowlist addresses
           ("EI TOHI testida — meil pole õigustatud huvi"). Mirrors the server
           guard in KinnistusraamatAggregatorService + QuickValuationController. -->
      <Message
        v-if="landRegistryTestBlocked && !landRegistryPermissionBlocked"
        severity="error"
        :closable="false"
        data-testid="short-report-kr-test-blocked"
        class="border border-red-300"
      >
        <div class="flex items-start gap-2">
          <span class="pi pi-exclamation-triangle text-red-600 mt-0.5"></span>
          <div class="flex flex-col gap-1">
            <span class="font-semibold">
              {{ $t('messages.quickValuation.shortReport.landRegistry.testGuardBlockedTitle') }}
            </span>
            <span class="text-sm">
              {{ $t('messages.quickValuation.shortReport.landRegistry.testGuardBlocked') }}
            </span>
          </div>
        </div>
      </Message>
      <div
        v-if="hasExistingLandRegistry && !landRegistryTestBlocked && !landRegistryPermissionBlocked"
        class="flex items-start gap-2 pl-6"
      >
        <Checkbox
          v-model="useExistingLandRegistry"
          binary
          input-id="short-report-kr-use-existing"
          data-testid="short-report-kr-use-existing"
          :disabled="isExporting || !addLandRegistry"
        />
        <label for="short-report-kr-use-existing" class="text-sm text-slate-600 leading-tight">
          {{ $t('messages.quickValuation.shortReport.landRegistry.useExisting', { date: formattedExistingKrDate }) }}
        </label>
      </div>
    </section>

    <!-- Section 5 — Progress (RED-212) -->
    <section
      v-if="phase !== 'idle'"
      class="mt-6 pt-4 border-t border-slate-200"
      data-testid="qv-short-report-progress"
    >
      <ProgressBar
        :mode="progressMode"
        :value="progressValue"
        :show-value="false"
        style="height: 6px"
      />
      <div class="mt-3 flex items-center justify-between text-xs text-slate-600">
        <span data-testid="qv-progress-label">{{ phaseLabel }}</span>
        <span v-if="phaseDetail" class="tabular-nums">{{ phaseDetail }}</span>
      </div>
      <!-- RED-212: hide the 3-step strip for historical downloads — the
           user is retrieving a saved report, not running a fresh pipeline. -->
      <ol
        v-if="mode !== 'historical'"
        class="mt-3 flex gap-4 text-[11px] text-slate-500"
        data-testid="qv-short-report-steps"
      >
        <li
          v-for="step in steps"
          :key="step.key"
          class="flex items-center gap-1.5"
          :class="stepClass(step.key)"
        >
          <span class="pi text-[10px]" :class="stepIcon(step.key)" />
          <span>{{ step.label }}</span>
        </li>
      </ol>
    </section>

    <template #footer>
      <Button
        :label="$t('messages.cancel')"
        severity="secondary"
        text
        :disabled="isExporting"
        @click="dialogVisible = false"
      />
      <Button
        data-testid="qv-short-report-generate"
        :label="$t('messages.quickValuation.shortReport.generate')"
        icon="pi pi-download"
        :disabled="!selectedTemplateId || isExporting"
        :loading="isExporting"
        @click="generate"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import Checkbox from 'primevue/checkbox'
import Message from 'primevue/message'
import ProgressBar from 'primevue/progressbar'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useShortReportDownload } from '@/composables/useShortReportDownload'
import { defaultTemplateId } from '@/services/quickValuationTemplates'
import { esgEvaluationText, useEsgStore } from '@/stores/useEsgStore'
import { useTransactionStore } from '@/stores/transaction'
import ShortReportPhotoSection from '@/components/quick-valuation/ShortReportPhotoSection.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  evaluation: {
    type: Object,
    required: true,
  },
  templates: {
    type: Array,
    default: () => [],
  },
  captureMaps: {
    type: Function,
    default: null,
  },
  hasExistingLandRegistry: {
    type: Boolean,
    default: false,
  },
  existingKrDate: {
    type: String,
    default: null,
  },
  landRegistryCost: {
    type: [Number, String],
    default: 6,
  },
  // RED-213: cadaster_id of the current evaluation, used together with the
  // shared kinnistusraamat prop (HandleInertiaRequests) to decide whether the
  // Kinnistusraamat checkbox should be enabled in non-LIVE environments.
  cadasterId: {
    type: String,
    default: null,
  },
  // RED-212: 'fresh' (post-result-page) keeps the original 3-step
  // "Kaardid → Andmestik → Aruanne" framing. 'historical' (history list)
  // reframes the UX as resurfacing a saved report — different title, no step
  // strip, and softer phase labels.
  mode: {
    type: String,
    default: 'fresh',
    validator: value => ['fresh', 'historical'].includes(value),
  },
  // RED-212: when the server reports full map coverage on disk
  // (`maps_cached.location` has all four canonical keys), the history dialog
  // sets this true so the OpenLayers capture — and its Maa-amet WMS tile
  // fetches — is skipped entirely. The server falls back to its cached PNGs.
  skipCapture: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:visible'])

const { t } = useI18n()
const toast = useToast()
const { download } = useShortReportDownload()
const esgStore = useEsgStore()
const transactionStore = useTransactionStore()

// Shape matches the Expert flow's useExportDetail payload so the server-side
// EsgItem mapping logic can be shared cleanly. `description` is auto-generated
// by esgStore.initializeEsgData off the /api/esg/all response, which is what
// we actually want the docx to show (e.g. "… 45-49 dB …") instead of the
// server-side static "not-found" fallback.
const collectEsgData = () => {
  const out = {}
  for (const [key, value] of Object.entries(esgStore.esgData ?? {})) {
    const user = value?.user ?? {}
    out[key] = {
      description: user.description ?? '',
      evaluation: user.evaluation ?? '',
    }
  }
  return out
}

// RED-355: the annex C rows the result page shows (impact „info puudub“,
// reference „-“ are the server's defaults for what is not sent). Null while
// the flag is off — the request then carries no ESG summary at all.
const esgAnnexCEnabled = useFeature('esg-annex-c')
const collectEsgSummary = () =>
  esgAnnexCEnabled.value
    ? esgStore.quickValuationAnnexCRows.map(row => ({
        key: row.key,
        evaluation: esgEvaluationText(row),
        source: row.source ?? '',
      }))
    : null

// Mirror useExportDetail's transactions payload so the backend can reuse
// DocxService::createTransactionsComparisonTable to render the full
// kohandustabel (the same 50-row Expert table with per-field vordlus/kohandus
// breakdowns). Comparison / selection / table_conf are all computed off the
// seeded transactionStore (see QuickValuationResult.vue::syncDetailStoreFromProps).
const collectTransactions = () => {
  const comparison = transactionStore.comparisonTableExport
  const selection = transactionStore.selectionTableExport
  if (!comparison?.cols?.length || !comparison?.rows?.length) {
    return null
  }
  return {
    comparison,
    selection,
    // RED-296: hiddenKeys mirrors useExportDetail — defensive backend filter
    // for user-hidden rows (QV clears hidden groups on hydration, so this is
    // normally empty here).
    table_conf: {
      ...(transactionStore.markedTableConf ?? {}),
      hiddenKeys: [...transactionStore.hiddenComparisonKeys],
    },
    selectedPriceBase: transactionStore.selectedPriceBase ?? 'tehingusumma',
  }
}

const selectedTemplateId = ref(null)
const livePhotoCount = ref(0)
const addLandRegistry = ref(false)
const useExistingLandRegistry = ref(true)

// RED-213: pull the test-environment guard config from Inertia shared props.
// `isLive` short-circuits to allowed in production; otherwise the cadaster
// must match an entry on `allowedCadasterIds` (defaults to the two e2e
// fixture pairs — Peterburi tee 9-1 + Harku tn 22).
const page = usePage()
const landRegistryTestBlocked = computed(() => {
  const guard = page.props?.kinnistusraamat
  if (!guard || guard.isLive) return false
  const allowed = Array.isArray(guard.allowedCadasterIds) ? guard.allowedCadasterIds : []
  return !allowed.includes(props.cadasterId)
})

// RED-220: Spatie `external-services` permission gate. When false the
// Kinnistusraamat checkbox is shown but disabled and a warn banner explains
// the situation (ask the admin or attach the excerpt manually). Sourced from
// `auth.can` shared via HandleInertiaRequests::resolvePermissions().
const landRegistryPermissionBlocked = computed(() => {
  return !page.props?.auth?.can?.['external-services']
})

// Keep the addLandRegistry checkbox in sync when blocked — never let a stale
// `true` value sneak past the disabled state into the submit payload.
watch([landRegistryTestBlocked, landRegistryPermissionBlocked], ([testBlocked, permBlocked]) => {
  if (testBlocked || permBlocked) {
    addLandRegistry.value = false
  }
}, { immediate: true })

// RED-212: phase state machine. Honest progress reporting needs three different
// truth sources (capture / upload / server-processing) so a single boolean
// hides too much. See plan doc for full rationale.
//   'idle'       — dialog open, nothing running
//   'capturing'  — running QvMapCaptureHost.captureAll() (determinate, n/12)
//   'uploading'  — axios POST in flight, onUploadProgress firing (determinate, %)
//   'processing' — upload complete, server building DOCX (indeterminate)
//   'done'       — DOCX downloaded, dialog about to close
//   'error'      — something failed; toast already shown
const phase = ref('idle')
const captureProgress = ref({ completed: 0, total: 12 })
const uploadProgress = ref(0)

// Decoupled from `phase` so the visual state machine can flash 'done' for
// momentum (see onDownloadProgress in generate()) without enabling the button
// while axios is still streaming the response and link.click() hasn't fired
// yet — clicking again during that window triggered a second full export.
const exportInFlight = ref(false)

const isExporting = computed(() => exportInFlight.value)

const determinatePhases = ['capturing', 'uploading']
const progressMode = computed(() =>
  determinatePhases.includes(phase.value) ? 'determinate' : 'indeterminate',
)

const progressValue = computed(() => {
  if (phase.value === 'capturing') {
    const { completed, total } = captureProgress.value
    if (!total) return 0
    return Math.round((completed / total) * 100)
  }
  if (phase.value === 'uploading') {
    return uploadProgress.value
  }
  if (phase.value === 'done') {
    return 100
  }
  return 0
})

const dialogHeader = computed(() =>
  props.mode === 'historical'
    ? t('messages.quickValuation.shortReport.titleHistorical')
    : t('messages.quickValuation.shortReport.title'),
)

// RED-212: historical mode reframes the wording as "resurfacing a saved
// report" instead of "compiling a fresh one". Falls back to the fresh string
// if a historical key is ever missing from a locale.
const phaseLabel = computed(() => {
  const base = 'messages.quickValuation.shortReport.progress'
  const historicalBase = 'messages.quickValuation.shortReport.historical.progress'
  const key = phase.value
  if (!['capturing', 'uploading', 'processing', 'done'].includes(key)) {
    return ''
  }
  if (key === 'done') {
    return t(`${base}.done`)
  }
  if (props.mode === 'historical') {
    return t(`${historicalBase}.${key}`)
  }
  return t(`${base}.${key}`)
})

const phaseDetail = computed(() => {
  if (phase.value === 'capturing') {
    const { completed, total } = captureProgress.value
    return `${completed} / ${total}`
  }
  if (phase.value === 'uploading') {
    return `${uploadProgress.value}%`
  }
  return ''
})

const steps = computed(() => [
  {
    key: 'capture',
    label: t('messages.quickValuation.shortReport.progress.steps.capture'),
  },
  {
    key: 'upload',
    label: t('messages.quickValuation.shortReport.progress.steps.upload'),
  },
  {
    key: 'process',
    label: t('messages.quickValuation.shortReport.progress.steps.process'),
  },
])

const stepStatus = (key) => {
  const order = ['capture', 'upload', 'process']
  const phaseToStep = {
    capturing: 'capture',
    uploading: 'upload',
    processing: 'process',
    done: 'done',
  }
  const currentStep = phaseToStep[phase.value]
  if (!currentStep) return 'pending'
  if (currentStep === 'done') return 'complete'
  const currentIdx = order.indexOf(currentStep)
  const myIdx = order.indexOf(key)
  if (myIdx < currentIdx) return 'complete'
  if (myIdx === currentIdx) return 'active'
  return 'pending'
}

const stepClass = (key) => {
  const status = stepStatus(key)
  if (status === 'complete') return 'text-emerald-600 font-medium'
  if (status === 'active') return 'text-primary font-medium'
  return 'text-slate-400'
}

const stepIcon = (key) => {
  const status = stepStatus(key)
  if (status === 'complete') return 'pi-check-circle'
  if (status === 'active') return 'pi-circle-fill'
  return 'pi-circle'
}

const resetProgress = () => {
  phase.value = 'idle'
  captureProgress.value = { completed: 0, total: 12 }
  uploadProgress.value = 0
  exportInFlight.value = false
}

const formattedExistingKrDate = computed(() => {
  if (!props.existingKrDate) return ''
  const d = new Date(props.existingKrDate)
  return Number.isNaN(d.getTime()) ? props.existingKrDate : d.toLocaleDateString('et-EE')
})

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

const comparableCount = computed(() => {
  return props.evaluation?.prediction?.similar_properties?.length ?? 0
})

// RED-356: a direct result (statistical-model mall) has no comparables, so
// behind the flag "Sarnased tehingud (0)" is not promised in the summary.
const statisticalReportEnabled = useFeature('qv-statistical-report')
const showComparablesLine = computed(() =>
  comparableCount.value > 0 || !statisticalReportEnabled.value,
)

// Auto-select the only template (common case: one company-wide template), or
// on a comparables result offered both malls the kohandustabel one (RED-356).
// `immediate`: the history page mounts this dialog already visible (v-if on
// its row), so a change-only watch never pre-selected there.
watch(
  () => props.visible,
  (visible) => {
    const defaultId = visible ? defaultTemplateId(props.templates) : null
    if (defaultId !== null) {
      selectedTemplateId.value = defaultId
    }
  },
  { immediate: true },
)

// RED-210: profiling flag. Gate on `?profile=1` so normal users don't see
// console spam, but perf work can turn it on and get real numbers.
const profileMode = () =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('profile')

const timed = async (label, fn) => {
  if (!profileMode()) return fn()
  const started = performance.now()
  try {
    return await fn()
  } finally {
    const elapsed = (performance.now() - started).toFixed(1)
    console.info(`[qv-export] ${label}: ${elapsed}ms`)
  }
}

const generate = async () => {
  // Re-entry guard: even if some path re-enables the button mid-export,
  // a duplicate click is a no-op rather than a second full POST.
  if (exportInFlight.value) return
  if (!selectedTemplateId.value) return

  resetProgress()
  exportInFlight.value = true
  const totalStart = profileMode() ? performance.now() : 0
  try {
    // --- Phase 1: capture ---
    // RED-212: when the server confirms full map coverage on disk
    // (`skipCapture`), bypass the OpenLayers capture entirely. The server's
    // map storage will fill every key from cache, saving a fresh
    // Maa-amet WMS round trip and visible "Kaardistame…" flicker.
    let maps = {}
    if (typeof props.captureMaps === 'function' && !props.skipCapture) {
      phase.value = 'capturing'
      try {
        maps = await timed('capture', () => props.captureMaps((progress) => {
          if (progress?.total) {
            captureProgress.value = {
              completed: progress.completed,
              total: progress.total,
            }
          }
        })) ?? {}
      } catch (err) {
        // Capture failed but we can still produce a DOCX with blank map
        // placeholders — fillMaps() handles missing paths gracefully.
        console.warn('[ShortReportDialog] map capture failed', err)
        maps = {}
      }
    }

    // --- Phase 2 & 3: upload, then server processing ---
    phase.value = 'uploading'
    let switchedToProcessing = false
    const { landRegistrySource } = await timed('upload+generate', () => download({
      evaluationId: props.evaluation.id,
      templateId: selectedTemplateId.value,
      maps,
      esg: collectEsgData(),
      esgSummary: collectEsgSummary(),
      transactions: collectTransactions(),
      addLandRegistry: addLandRegistry.value,
      useExistingLandRegistry: useExistingLandRegistry.value,
      onUploadProgress: (event) => {
        const total = event.total || event.loaded
        const pct = total ? Math.round((event.loaded / total) * 100) : 0
        uploadProgress.value = pct
        // Flip to indeterminate processing as soon as upload completes —
        // axios fires this before the server starts work, so the gap between
        // 100% upload and the first download byte is genuine server time.
        if (pct >= 100 && !switchedToProcessing) {
          switchedToProcessing = true
          phase.value = 'processing'
        }
      },
      onDownloadProgress: () => {
        // Server has flushed first byte → finished thinking. Transition out
        // of indeterminate so the user sees momentum just before the file
        // arrives (download is ~1s on broadband).
        if (phase.value === 'processing') {
          phase.value = 'done'
        }
      },
    }))

    phase.value = 'done'

    if (addLandRegistry.value) {
      showLandRegistryToast(landRegistrySource)
    }

    if (profileMode()) {
      const total = (performance.now() - totalStart).toFixed(1)
      console.info(`[qv-export] total click→download: ${total}ms`)
    }

    dialogVisible.value = false
  } catch {
    phase.value = 'error'
    toast.add({
      severity: 'error',
      summary: t('messages.error'),
      detail: t('messages.quickValuation.shortReport.error'),
      life: 5000,
    })
  } finally {
    exportInFlight.value = false
  }
}

const showLandRegistryToast = (source) => {
  if (source === 'unavailable') {
    toast.add({
      severity: 'warn',
      summary: t('messages.quickValuation.shortReport.landRegistry.unavailableTitle'),
      detail: t('messages.quickValuation.shortReport.landRegistry.unavailable'),
      life: 6000,
    })
  } else if (source === 'redis_cache' || source === 'evaluation_persistence') {
    toast.add({
      severity: 'success',
      summary: t('messages.quickValuation.shortReport.landRegistry.reusedTitle'),
      detail: t('messages.quickValuation.shortReport.landRegistry.reused'),
      life: 4000,
    })
  } else if (source === 'fresh_soap') {
    toast.add({
      severity: 'info',
      summary: t('messages.quickValuation.shortReport.landRegistry.freshTitle'),
      detail: t('messages.quickValuation.shortReport.landRegistry.fresh', { cost: props.landRegistryCost }),
      life: 5000,
    })
  }
}
</script>
